import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Configuration } from 'src/app/app.constants';
import { ObjectTypes } from '../enums/objectTypes';
import { FilterTypeSelectorViewModel } from '../viewModels/filters/filterTypeSelectorViewModel';
import { FilterTypeSelectorObjectMappingViewModel } from '../viewModels/filters/filterTypeSelectorObjectMappingViewModel';

@Injectable({
  providedIn: 'root',
})
export class SortingFiltersService {

  // Map to store sorting modal filter types by object type key
  private sortingModalMap: Map<ObjectTypes, FilterTypeSelectorViewModel[]> = new Map<ObjectTypes, FilterTypeSelectorViewModel[]>();

  // Subject to notify subscribers when sorting modal filter types are updated
  private readonly _sortingModalUpdated = new BehaviorSubject<ObjectTypes[]>([]);
  public readonly sortingModalUpdated$ = this._sortingModalUpdated.asObservable();

  constructor(
    private http: HttpClient,
    private configuration: Configuration
  ) {
    
  }

  /**
   * Getter that dynamically builds the url each time.
   * This ensures the URL updates when the backend API changes (e.g., account switching).
   */
  private get url(): string {
    return this.configuration.buildEndpoint(`Filters/`);
  }

  /**
   * Initializes the sorting modal filter types cache with provided mappings
   * @param sortingModalMappings - Array of sorting modal mappings from base config
   */
  public initializeSortingModalMappings(sortingModalMappings: FilterTypeSelectorObjectMappingViewModel[]): void {
    if (!sortingModalMappings || sortingModalMappings.length === 0) return;

    const updatedKeys: ObjectTypes[] = [];
    this.sortingModalMap.clear();

    sortingModalMappings.forEach(mapping => {
      if (mapping && mapping.objectType) {
        const filterSelectors = mapping.filterTypeSelectors || [];
        this.sortingModalMap.set(mapping.objectType, filterSelectors);
        updatedKeys.push(mapping.objectType);
      }
    });

    if (updatedKeys.length > 0) {
      this._sortingModalUpdated.next(updatedKeys);
    }
  }

  /**
   * Get Sorting and custom column modal filter types
   * Checks cache only, returns empty array if object type not found in cache
   * @param objectType - The object type to get filter types for
   * @returns Observable of FilterTypeSelectorViewModel array or empty array if not in cache
   */
  public getSortingModalFilterTypes(objectType: ObjectTypes): Observable<FilterTypeSelectorViewModel[]> {
    if (this.sortingModalMap.has(objectType)) {
      return of(this.sortingModalMap.get(objectType));
    } else {
      return of([]);
    }
  }

  /**
   * Clear preselected Filter Types in the sorting modal
   * @param objectType the object type for which the sorting modal is opened
   * @returns Observable of updated filter types
   */
  public clearSortingModalVMByObjectType(objectType: ObjectTypes): Observable<FilterTypeSelectorViewModel[]> {
    if (!objectType) return of([]);

    if (this.sortingModalMap.has(objectType)) {
      this.sortingModalMap.delete(objectType);
    }

    return this.getLatestUpdated(objectType);
  }

  public getLatestUpdated(objectType: ObjectTypes): Observable<FilterTypeSelectorViewModel[]> {
    return this.http.post<FilterTypeSelectorViewModel[]>(`${this.url}sortingmodal`, [objectType]).pipe(
      map((result) => {
        this.sortingModalMap.set(objectType, result);
        this._sortingModalUpdated.next([objectType]);
        return result;
      })
    );
  }
}
